// Brand logo service for PoinVoy
export interface BrandLogo {
  name: string;
  logo: string;
  fallbackIcon: string;
  brandColor: string;
}

// Brand logo mapping with proper assets and fallbacks
export const brandLogos: Record<string, BrandLogo> = {
  'united-mileageplus': {
    name: 'United MileagePlus',
    logo: '/images/logos/united.svg',
    fallbackIcon: '✈️',
    brandColor: '#0066CC',
  },
  'chase-sapphire-reserve': {
    name: 'Chase Sapphire Reserve',
    logo: '/images/logos/chase-sapphire.svg',
    fallbackIcon: '💳',
    brandColor: '#1A365D',
  },
  'marriott-bonvoy': {
    name: 'Marriott Bonvoy',
    logo: '/images/logos/marriott.svg',
    fallbackIcon: '🏨',
    brandColor: '#C41E3A',
  },
  'delta-skymiles': {
    name: 'Delta SkyMiles',
    logo: '/images/logos/delta.svg',
    fallbackIcon: '✈️',
    brandColor: '#C8102E',
  },
  'amex-platinum': {
    name: 'American Express Platinum',
    logo: '/images/logos/amex.svg',
    fallbackIcon: '💳',
    brandColor: '#006FCF',
  },
  'hilton-honors': {
    name: 'Hilton Honors',
    logo: '/images/logos/hilton.svg',
    fallbackIcon: '🏨',
    brandColor: '#0F70B7',
  },
  'southwest-rapid-rewards': {
    name: 'Southwest Rapid Rewards',
    logo: '/images/logos/southwest.svg',
    fallbackIcon: '✈️',
    brandColor: '#F9B612',
  },
  'hyatt-world': {
    name: 'World of Hyatt',
    logo: '/images/logos/hyatt.svg',
    fallbackIcon: '🏨',
    brandColor: '#C8A882',
  },
  'capital-one-venture': {
    name: 'Capital One Venture',
    logo: '/images/logos/capital-one.svg',
    fallbackIcon: '💳',
    brandColor: '#004879',
  },
  'alaska-mileageplan': {
    name: 'Alaska Mileage Plan',
    logo: '/images/logos/alaska.svg',
    fallbackIcon: '✈️',
    brandColor: '#0074A2',
  },
};

// Get brand logo with fallback handling
export const getBrandLogo = (programKey: string): BrandLogo => {
  return brandLogos[programKey] || {
    name: 'Unknown Program',
    logo: '',
    fallbackIcon: '⭐',
    brandColor: '#6366f1',
  };
};

// Generate SVG logo as fallback
export const generateFallbackLogo = (
  text: string, 
  backgroundColor: string, 
  textColor: string = '#FFFFFF'
): string => {
  const svg = `
    <svg width="60" height="60" xmlns="http://www.w3.org/2000/svg">
      <rect width="60" height="60" rx="16" fill="${backgroundColor}"/>
      <text x="30" y="38" font-family="Inter, sans-serif" font-size="14" font-weight="600" 
            text-anchor="middle" fill="${textColor}">${text}</text>
    </svg>
  `;
  // Use encodeURIComponent for Unicode characters instead of btoa
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// Create beautiful branded SVG logos
export const createBrandedSVG = (
  brandKey: string,
  icon: string,
  backgroundColor: string
): string => {
  const svg = `
    <svg width="60" height="60" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="grad-${brandKey}" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:${backgroundColor};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${backgroundColor}CC;stop-opacity:1" />
        </linearGradient>
        <filter id="shadow-${brandKey}">
          <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
        </filter>
      </defs>
      <rect width="60" height="60" rx="16" fill="url(#grad-${brandKey})" filter="url(#shadow-${brandKey})"/>
      <text x="30" y="38" font-family="Inter, sans-serif" font-size="24" 
            text-anchor="middle" fill="#FFFFFF">${icon}</text>
    </svg>
  `;
  // Use encodeURIComponent for Unicode characters instead of btoa
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};